import { Pos, Side } from 'widgets/LineWidget/LineWidget.types';

/**
 * Returns the rotated position using the origin position and the angle.
 *
 * @param pos
 * @param origin
 * @param angleInDegrees
 * @returns
 */
export const rotatePointAroundOrigin = (pos: Pos, origin: Pos, angleInDegrees: number): Pos => {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  const centerX = origin.xPx;
  const centerY = origin.yPx;

  // Calculate the offset of the point from the origin
  const offsetX = pos.xPx - centerX;
  const offsetY = pos.yPx - centerY;

  // Rotate the offset around the origin
  const rotatedOffsetX = Math.cos(angleInRadians) * offsetX - Math.sin(angleInRadians) * offsetY;
  const rotatedOffsetY = Math.sin(angleInRadians) * offsetX + Math.cos(angleInRadians) * offsetY;

  // Add the rotated offset back to the origin to get the new position
  return {
    xPx: centerX + rotatedOffsetX,
    yPx: centerY + rotatedOffsetY,
  };
};

/**
 * Calculates the center coordinates of a circle given a point on its edge.
 *
 * @param x The x-coordinate of the point on the edge of the circle.
 * @param y The y-coordinate of the point on the edge of the circle.
 * @param radius The radius of the circle.
 * @param angleInDegrees The angle in degrees from the center of the circle to the point (x, y).
 * @returns An object containing the x and y coordinates of the circle's center, labeled as xPx and yPx.
 */
export const getCircleCenterFromPointOnEdge = (x: number, y: number, radius: number, angleInDegrees: number) => {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  const centerX = x - radius * Math.cos(angleInRadians);
  const centerY = y - radius * Math.sin(angleInRadians);
  return { xPx: centerX, yPx: centerY };
};

/**
 * Calculates the center of a rectangle from a given midpoint on one of its sides, taking into account the rectangle's rotation.
 *
 * @param width The width of the rectangle.
 * @param height The height of the rectangle.
 * @param midPoint A `Pos` object representing the midpoint on one of the rectangle's sides.
 * @param rotation The rotation of the rectangle in degrees.
 * @param side The side of the rectangle on which the midpoint is located, specified by the `Side` enum.
 * @returns A `Pos` object representing the x and y coordinates of the rectangle's center.
 *
 */
export const getRectCenterFromPointOnSide = (
  width: number,
  height: number,
  midPoint: Pos,
  rotation: number,
  side: Side,
): Pos => {
  const circleRadius = side === Side.NORTH || side === Side.SOUTH ? height / 2 : width / 2;
  let circleAngle = 0;
  if (side === Side.NORTH) {
    circleAngle = 90;
  } else if (side === Side.EAST) {
    circleAngle = 0;
  } else if (side === Side.SOUTH) {
    circleAngle = 270;
  } else if (side === Side.WEST) {
    circleAngle = 180;
  }
  return getCircleCenterFromPointOnEdge(midPoint.xPx, midPoint.yPx, circleRadius, rotation - circleAngle);
};
